import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useAnalytics } from '../../analytics';

// API base is centralized in lib/api

const AddToListModal = ({ isOpen, onClose, sandwichId, sandwichName, sourceReviewId = null }) => {
  const [userLists, setUserLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [selectedLists, setSelectedLists] = useState(new Set());
  const [message, setMessage] = useState('');
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();

  // Fetch user's lists when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchUserLists();
    }
  }, [isOpen, user]);

  const fetchUserLists = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/lists/me`);
      setUserLists(response.data.lists || []);
      
      // Check which lists already contain this sandwich
      const existingLists = new Set();
      response.data.lists.forEach(list => {
        if (list.sandwiches && list.sandwiches.includes(sandwichId)) {
          existingLists.add(list.id);
        }
      });
      setSelectedLists(existingLists);
    } catch (error) {
      console.error('Error fetching user lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewList = async () => {
    if (!newListName.trim()) return;
    
    try {
      setCreating(true);
      const response = await api.post(
        `/lists`,
        {
          name: newListName,
          type: 'wishlist',
          is_public: true
        }
      );
      
      const newList = response.data.list;
      setUserLists(prev => [...prev, { ...newList, sandwich_count: 0, preview_images: [] }]);
      setNewListName('');
      setShowCreateNew(false);
      setMessage(`Created new list: ${newList.name}`);

      if (trackEvent) {
        trackEvent('list_created', {
          list_id: newList?.id,
          list_type: newList?.type || 'wishlist'
        });
      }
    } catch (error) {
      console.error('Error creating list:', error);
      setMessage('Failed to create list');
    } finally {
      setCreating(false);
    }
  };

  const handleListToggle = async (listId) => {
    try {
      const isSelected = selectedLists.has(listId);
      
      if (isSelected) {
        // Remove from list
        await api.delete(`/lists/${listId}/sandwiches/${sandwichId}`);
        setSelectedLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(listId);
          return newSet;
        });
        setMessage('Removed from list');

        if (trackEvent) {
          trackEvent('list_item_removed', {
            list_id: listId,
            sandwich_id: sandwichId,
            source_review_id_present: Boolean(sourceReviewId)
          });
        }
      } else {
        // Add to list
        const payload = { sandwich_id: sandwichId };
        if (sourceReviewId) {
          payload.source_review_id = sourceReviewId;
        }
        await api.post(
          `/lists/${listId}/sandwiches`,
          payload
        );
        setSelectedLists(prev => new Set([...prev, listId]));
        setMessage('Added to list');

        if (trackEvent) {
          trackEvent('list_item_added', {
            list_id: listId,
            sandwich_id: sandwichId,
            source_review_id_present: Boolean(sourceReviewId)
          });
        }
      }
    } catch (error) {
      console.error('Error updating list:', error);
      if (error.response?.status === 400 && error.response?.data?.detail === 'Sandwich already in list') {
        setMessage('Already in list');
      } else {
        setMessage('Failed to update list');
      }
    }
  };

  const handleClose = () => {
    setShowCreateNew(false);
    setNewListName('');
    setMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Add to List
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {sandwichName}
          </p>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center">
              <div className="text-gray-500">Loading your lists...</div>
            </div>
          ) : (
            <div className="p-6">
              {/* Create New List Section */}
              {!showCreateNew ? (
                <button
                  onClick={() => setShowCreateNew(true)}
                  className="w-full flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-passion-red hover:bg-red-50 transition-colors mb-4"
                >
                  <span className="text-passion-red text-lg mr-2">+</span>
                  <span className="text-gray-700">Create New List</span>
                </button>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 mb-4">
                  <input
                    type="text"
                    placeholder="List name"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded mb-3"
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCreateNewList}
                      disabled={!newListName.trim() || creating}
                      className="px-4 py-2 bg-passion-red text-white rounded hover:bg-red-600 disabled:opacity-50"
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateNew(false);
                        setNewListName('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Existing Lists */}
              {userLists.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <p>No lists yet</p>
                  <p className="text-sm">Create your first list above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Your Lists</h4>
                  {userLists.map(list => (
                    <label
                      key={list.id}
                      className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedLists.has(list.id)}
                        onChange={() => handleListToggle(list.id)}
                        className="mr-3 w-4 h-4 text-passion-red focus:ring-passion-red border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{list.name}</div>
                        <div className="text-sm text-gray-500">
                          {list.sandwich_count} sandwich{list.sandwich_count !== 1 ? 'es' : ''}
                        </div>
                      </div>
                      {selectedLists.has(list.id) && (
                        <span className="text-passion-red text-sm">✓</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {message && (
          <div className="p-4 bg-green-50 border-t border-gray-200">
            <p className="text-sm text-green-700">{message}</p>
          </div>
        )}

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-passion-red text-white rounded-lg hover:bg-red-600"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToListModal;


